import { FilterXSS, type IFilterXSSOptions } from 'xss';

/**
 * Allowlist matches Tiptap StarterKit + Link extension exactly so legitimate
 * editor output passes through unchanged. Anything outside the allowlist is
 * stripped — covers stored-XSS via crafted POST bodies that bypass the
 * composer (e.g. `curl` directly to /api/clients/:id with a malicious
 * client_information_html payload).
 *
 * Used at write-time in workers AND at render-time in <RichTextRender />,
 * so a regression in either path is caught by the other.
 *
 * Implementation note: backed by `xss` (leizongmin/js-xss) because it is
 * pure JS with only `cssfilter` as a transitive dep — no `postcss` /
 * `htmlparser2` / native deps. Safe to bundle into the Cloudflare Workers
 * runtime via esbuild. The previous impl used `sanitize-html` and broke
 * Pages deploys with `Dynamic require of "url" is not supported` because
 * `sanitize-html` transitively pulls `postcss` which uses runtime
 * `require()` — workerd refuses dynamic `require`. xss does only static
 * requires that esbuild can resolve at bundle time.
 */

const ALLOWED_TAGS = [
  'p',
  'br',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'strong',
  'em',
  's',
  'u',
  'code',
  'pre',
  'blockquote',
  'ol',
  'ul',
  'li',
  'a',
] as const;

// xss `whiteList` shape: { tagName: ['allowedAttr1', ...] }. Tags absent
// from this map are stripped (when stripIgnoreTag is true) or escaped
// (default). For <a>, we allow href only — rel + target are injected
// post-process below so they always carry the safe defaults regardless of
// what the caller sent.
const WHITELIST: Record<string, string[]> = Object.fromEntries(
  ALLOWED_TAGS.map((tag) => [tag, tag === 'a' ? ['href'] : []]),
);

export const TIPTAP_SANITIZE_OPTIONS: IFilterXSSOptions = {
  whiteList: WHITELIST,
  // Strip any tag not in WHITELIST entirely (default behaviour escapes them
  // as &lt;tag&gt; which would render as visible text in the prose-jywer
  // CSS — uglier than just dropping).
  stripIgnoreTag: true,
  // For these tags, drop both the tag AND its inner text (so
  // <script>alert(1)</script> becomes empty, not "alert(1)").
  stripIgnoreTagBody: ['script', 'style', 'iframe', 'object', 'embed', 'form'],
  allowCommentTag: false,
  // Filter URL schemes on <a href>. xss's default safeAttrValue accepts a
  // wider range; we tighten to http/https/mailto only.
  safeAttrValue(tag, name, value) {
    if (tag === 'a' && name === 'href') {
      const trimmed = value.trim().toLowerCase();
      if (
        !trimmed.startsWith('http://') &&
        !trimmed.startsWith('https://') &&
        !trimmed.startsWith('mailto:')
      ) {
        return '';
      }
      return value.trim();
    }
    return value;
  },
};

const filter = new FilterXSS(TIPTAP_SANITIZE_OPTIONS);

/**
 * Inject rel + target on every <a> tag so links always carry the
 * defence-in-depth defaults regardless of how the caller wrote them.
 * Safe regex: xss has already stripped any unbalanced / nested tag
 * structures, so the input here is well-formed.
 */
function enforceLinkSafety(html: string): string {
  return html.replace(/<a\b([^>]*)>/gi, (_match, attrs: string) => {
    let updated = attrs;
    if (!/\brel\s*=/i.test(updated)) {
      updated += ' rel="noopener noreferrer"';
    }
    if (!/\btarget\s*=/i.test(updated)) {
      updated += ' target="_blank"';
    }
    return `<a${updated}>`;
  });
}

export function sanitizeTiptapHtml(html: string): string {
  if (!html) return html;
  return enforceLinkSafety(filter.process(html));
}

/**
 * Variant for Zod transforms that pass nullable / optional rich text.
 * Null and undefined pass through unchanged so callers can keep
 * `.nullable().optional()` semantics.
 */
export function sanitizeTiptapHtmlOrNull<T extends string | null | undefined>(value: T): T {
  if (value === null || value === undefined) return value;
  return sanitizeTiptapHtml(value as string) as T;
}
